import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import {
  CalendarDays,
  CalendarRange,
  Euro,
  Wallet,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

import Sidebar from "shared/Sidebar/Sidebar";
import { useReservationStore } from "hooks";

type DiaStat = { dia: string; etiqueta: string; reservas: number; facturado: number };
type SalaStat = { sala: string; reservas: number; facturado: number };
type DashboardStats = {
  ok: boolean;
  mes: string;
  hoy: { reservas: number; facturado: number };
  mesStats: { reservas: number; facturado: number };
  serieDiaria: DiaStat[];
  porSala: SalaStat[];
};

const eur = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const eur2 = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MESES_LARGO = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const SALA_COLORS = ["#a78bfa", "#8b5cf6", "#22d3ee", "#34d399", "#fbbf24", "#f472b6", "#60a5fa", "#f87171"];

const currentMonth = () => {
  const f = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid", year: "numeric", month: "2-digit" });
  return f.format(new Date()); // "YYYY-MM"
};

const parts = (m: string) => {
  const [y, mm] = m.split("-").map(Number);
  return { year: y, idx: mm - 1 };
};
const toMonth = (year: number, idx: number) => `${year}-${String(idx + 1).padStart(2, "0")}`;
// El admin puede consultar hasta 3 meses hacia adelante (para ver las reservas futuras),
// además del mes en curso y los pasados.
const MONTHS_AHEAD = 3;
const maxMonth = () => {
  const { year, idx } = parts(currentMonth());
  const d = new Date(year, idx + MONTHS_AHEAD, 1);
  return toMonth(d.getFullYear(), d.getMonth());
};
const isAfterMax = (year: number, idx: number) => toMonth(year, idx) > maxMonth();

// ---- Selector de mes (glass, sin input nativo) ----
const MonthPicker = ({ value, onChange }: { value: string; onChange: (m: string) => void }) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parts(value).year);
  const ref = useRef<HTMLDivElement>(null);
  const { year, idx } = parts(value);

  useEffect(() => {
    if (open) setViewYear(parts(value).year);
  }, [open, value]);

  const shift = (delta: number) => {
    const d = new Date(year, idx + delta, 1);
    const next = toMonth(d.getFullYear(), d.getMonth());
    if (next <= maxMonth()) onChange(next);
  };

  const label = `${MESES_LARGO[idx]} ${year}`;
  const nextDisabled = isAfterMax(new Date(year, idx + 1, 1).getFullYear(), new Date(year, idx + 1, 1).getMonth());

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => shift(-1)}
          aria-label="Mes anterior"
          className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-slate-300 transition hover:bg-white/10 hover:text-white"
        >
          <ChevronLeft size={18} />
        </button>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex min-w-[150px] items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold capitalize text-white transition hover:bg-white/10"
        >
          {label}
          <ChevronDown size={16} className={`text-slate-400 transition ${open ? "rotate-180" : ""}`} />
        </button>

        <button
          type="button"
          onClick={() => shift(1)}
          disabled={nextDisabled}
          aria-label="Mes siguiente"
          className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-slate-300 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/5"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {open && (
        <>
          <button type="button" aria-label="Cerrar selector" className="fixed inset-0 z-30 cursor-default" onClick={() => setOpen(false)} />
          <div
            role="dialog"
            className="absolute right-0 z-40 mt-2 w-64 rounded-2xl border border-white/10 bg-[#1a1530]/90 p-3 shadow-2xl backdrop-blur-xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewYear((y) => y - 1)}
                aria-label="Año anterior"
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 transition hover:bg-white/10"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm font-semibold text-white">{viewYear}</span>
              <button
                type="button"
                onClick={() => setViewYear((y) => y + 1)}
                disabled={viewYear >= parts(maxMonth()).year}
                aria-label="Año siguiente"
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 transition hover:bg-white/10 disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MESES.map((m, i) => {
                const selected = viewYear === year && i === idx;
                const disabled = isAfterMax(viewYear, i);
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onChange(toMonth(viewYear, i));
                      setOpen(false);
                    }}
                    className={`rounded-lg py-2 text-sm font-medium transition ${
                      selected
                        ? "bg-violet-500 text-white shadow-lg shadow-violet-500/30"
                        : "text-slate-300 hover:bg-white/10"
                    } disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({
  label,
  value,
  Icon,
  tint,
}: {
  label: string;
  value: string;
  Icon: typeof CalendarDays;
  tint: string;
}) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-xl backdrop-blur-xl transition hover:border-white/20 hover:bg-white/[0.06]">
    <div className="flex items-center gap-3">
      <span className={`grid h-11 w-11 place-items-center rounded-xl ${tint}`}>
        <Icon size={20} />
      </span>
      <p className="text-sm font-medium text-slate-400">{label}</p>
    </div>
    <p className="mt-4 text-2xl font-bold text-white sm:text-3xl">{value}</p>
  </div>
);

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section
    className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 shadow-xl backdrop-blur-xl sm:p-5"
    aria-label={title}
  >
    <h2 className="mb-4 text-base font-semibold text-white">{title}</h2>
    {children}
  </section>
);

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(26,21,48,0.95)",
  color: "#fff",
  fontSize: 13,
  boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
};
// En recharts el color del texto de los datos (itemStyle) y de la etiqueta (labelStyle)
// no lo hereda de contentStyle; sin esto el texto sale en color oscuro y no se ve.
const tooltipItemStyle = { color: "#fff" };
const tooltipLabelStyle = { color: "#cbd5e1", marginBottom: 4, fontWeight: 600 };

const PageDashboard = () => {
  const [sideMenuIsExpand, setSideMenuIsExpand] = useState(true);
  const [month, setMonth] = useState<string>(currentMonth());
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const { getDashboardStats } = useReservationStore();

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      const data = await getDashboardStats({ date: `${month}-15` });
      if (active) {
        setStats(data);
        setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const hasData = useMemo(
    () => !!stats && (stats.mesStats?.reservas > 0 || stats.porSala?.length > 0),
    [stats]
  );

  const { year, idx } = parts(month);
  const monthLabel = `${MESES_LARGO[idx]} ${year}`;

  return (
    <div>
      <Sidebar setExpand={setSideMenuIsExpand} />
      {/* content */}
      <div
        className={`relative min-h-screen bg-[#0c0a16] text-white transition-all duration-300 ease-in-out ${
          sideMenuIsExpand ? "md:ml-72" : "md:ml-20"
        }`}
      >
        {/* glow de fondo */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/3 h-96 w-96 rounded-full bg-violet-600/20 blur-[120px]" />
          <div className="absolute top-20 right-10 h-72 w-72 rounded-full bg-indigo-600/10 blur-[120px]" />
        </div>

        <div className="relative mx-auto max-w-screen-2xl px-4 pb-8 pt-16 sm:px-6 md:p-8 2xl:p-10">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white sm:text-3xl">Dashboard</h1>
              <p className="text-sm text-slate-400">Resumen de reservas y facturación</p>
            </div>
            <MonthPicker value={month} onChange={setMonth} />
          </div>

          {/* KPIs HOY */}
          <section className="mb-6">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-violet-400" aria-hidden="true" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Hoy</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <KpiCard label="Reservas hoy" value={loading ? "—" : String(stats?.hoy.reservas ?? 0)} Icon={CalendarDays} tint="bg-violet-500/15 text-violet-300" />
              <KpiCard label="Facturado hoy" value={loading ? "—" : eur.format(stats?.hoy.facturado ?? 0)} Icon={Euro} tint="bg-emerald-500/15 text-emerald-300" />
            </div>
          </section>

          {/* KPIs + gráficos del MES seleccionado */}
          <div className="mb-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-indigo-400" aria-hidden="true" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Mes · <span className="capitalize text-slate-200">{monthLabel}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <KpiCard label="Reservas del mes" value={loading ? "—" : String(stats?.mesStats.reservas ?? 0)} Icon={CalendarRange} tint="bg-indigo-500/15 text-indigo-300" />
            <KpiCard label="Facturado del mes" value={loading ? "—" : eur.format(stats?.mesStats.facturado ?? 0)} Icon={Wallet} tint="bg-amber-500/15 text-amber-300" />
          </div>

          {/* Charts */}
          {loading ? (
            <div className="mt-6 grid animate-pulse grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="h-72 rounded-2xl border border-white/10 bg-white/[0.04] lg:col-span-2" />
              <div className="h-72 rounded-2xl border border-white/10 bg-white/[0.04]" />
              <div className="h-72 rounded-2xl border border-white/10 bg-white/[0.04]" />
            </div>
          ) : !hasData ? (
            <div className="mt-6 rounded-2xl border border-dashed border-white/15 bg-white/[0.03] p-10 text-center backdrop-blur-xl">
              <p className="text-slate-400">No hay reservas registradas para este mes.</p>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="lg:col-span-2">
                <ChartCard title="Reservas por día">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={stats?.serieDiaria} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#a78bfa" />
                          <stop offset="100%" stopColor="#7c3aed" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                      <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} formatter={(v: number) => [v, "Reservas"]} labelFormatter={(l) => `Día ${l}`} contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                      <Bar dataKey="reservas" fill="url(#barGrad)" radius={[5, 5, 0, 0]} maxBarSize={30} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              <ChartCard title="Facturación por día (€)">
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={stats?.serieDiaria} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="facGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ stroke: "rgba(255,255,255,0.15)" }} formatter={(v: number) => [eur2.format(v), "Facturado"]} labelFormatter={(l) => `Día ${l}`} contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                    <Area type="monotone" dataKey="facturado" stroke="#a78bfa" strokeWidth={2} fill="url(#facGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Reservas por sala">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={stats?.porSala} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="sala" width={90} tick={{ fontSize: 11, fill: "#cbd5e1" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} formatter={(v: number, _n, p: any) => [`${v} reservas · ${eur.format(p?.payload?.facturado ?? 0)}`, p?.payload?.sala]} contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                    <Bar dataKey="reservas" radius={[0, 5, 5, 0]} maxBarSize={26}>
                      {stats?.porSala.map((_, i) => (
                        <Cell key={i} fill={SALA_COLORS[i % SALA_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default PageDashboard;
